// What really happened, revealed once the game is over: the server's story, a chapter per night
// and day, with whether each player could trust what they were told and why not.
import type { StoryEntry } from "../protocol";

type Verdict = NonNullable<StoryEntry["verdict"]>;

const VERDICT: Record<Verdict, { label: string; className: string }> = {
  true: { label: "True", className: "chip-ok" },
  false: { label: "False", className: "chip-evil" },
  drunk: { label: "Drunk", className: "chip-evil" },
  poisoned: { label: "Poisoned", className: "chip-evil" },
  misregistered: { label: "Misregistered", className: "chip-warn" },
};

interface Chapter {
  key: string;
  title: string;
  entries: StoryEntry[];
}

function chapters(story: StoryEntry[]): Chapter[] {
  const out: Chapter[] = [];
  for (const entry of story) {
    const key = `${entry.phase}-${entry.number}`;
    const last = out[out.length - 1];
    if (last?.key === key) {
      last.entries.push(entry);
    } else {
      const title = entry.phase === "night" ? `Night ${entry.number}` : `Day ${entry.number}`;
      out.push({ key, title, entries: [entry] });
    }
  }
  return out;
}

export function GameStory({ story }: { story: StoryEntry[] }) {
  if (story.length === 0) return null;
  return (
    <section className="stack-sm" aria-labelledby="story-title">
      <h3 id="story-title">What happened</h3>
      {chapters(story).map((chapter) => (
        <div key={chapter.key} className="stack-sm">
          <span className="eyebrow">{chapter.title}</span>
          <ul className="story">
            {chapter.entries.map((entry, i) => (
              <li key={i}>
                {entry.text}
                {entry.verdict && (
                  <span className={`chip ${VERDICT[entry.verdict].className}`}>
                    {VERDICT[entry.verdict].label}
                  </span>
                )}
                {entry.note && <span className="story-note small muted">{entry.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
